import { useEffect, useRef, useState } from 'react';
import { Box, Grid, InputLabel, MenuItem, Paper, FormControl, Select } from '@material-ui/core';
import { Form } from '@unform/web';
import { toast } from 'react-toastify';
import { useHistory, useParams } from 'react-router-dom';
import {
  extractValidationErrors,
  GetAccredited,
  RegisterAccredited,
  ValidationError,
  signupAccreditedRegistrationForm,
  editAccreditedForm,
  GetPrograms,
  GetAccreditedByCnpj,
  ProgramAccredited,
  Program,
} from '@bforms-web/common';

import { ACCREDITEDS_ROUTE } from '../../../constants';
import { useLoading } from '../../../contexts/loading.context';
import Button from '../../Button';
import Input from '../../Input';
import PageTitle from '../../PageTitle';

import { useStyles } from './styles';
import { IMaskInputCEP, IMaskInputCNPJ } from '../../IMaskInput';

import { citiesDataList } from '../../../data/citiesData';  // Dados convertidos
import { City } from '../../../data/city';
import { KeysLocalStorage } from '../../../enums/keys-localstorage';

export type AccreditedRegisterProps = {
  getAccredited: GetAccredited;
  registerAccredited: RegisterAccredited;
  getPrograms: GetPrograms;
  getAccreditedByCnpj: GetAccreditedByCnpj;
};

function removeMask(input: string): string {
  return input.replace(/\D/g, ''); // Remove tudo o que não for número
}

export default function AccreditedRegister({ getAccredited, registerAccredited, getPrograms, getAccreditedByCnpj }: AccreditedRegisterProps): JSX.Element {
  const classes = useStyles();
  const formRef = useRef({} as any);
  const { isLoading, showLoading, hideLoading } = useLoading();
  const { push } = useHistory();
  const { id } = useParams<{ id: string }>();
  
  const [programs, setPrograms] = useState<Program[]>([]);
  const [selectedProgram, setSelectedProgram] = useState('');

  const [selectedState, setSelectedState] = useState<string>('');
  const [availableCities, setAvailableCities] = useState<City[]>([]);
  const [selectedCity, setSelectedCity] = useState<string>('');

  const [isFieldsEnabled, setIsFieldsEnabled] = useState(false);
  const [CNPJ, setCnpj] = useState('');

  const [isProgramDisabled, setIsProgramDisabled] = useState(false);

  const handleSubmit = async (data: any) => {
      showLoading();
      async function runAsync() {
        try {
          formRef.current.setErrors({});
          
          const cleanedData = {
            ...data,
            CNPJ: removeMask(data.CNPJ),
            programId: selectedProgram,
            state: selectedState,
            city: selectedCity,
          };
          
          if (!id) {
            await signupAccreditedRegistrationForm.validate(cleanedData, {
              abortEarly: false,
            });
          } else {
            await editAccreditedForm.validate(cleanedData, {
              abortEarly: false,
            });
          }
  
          await registerAccredited.execute({
            id,
            ...cleanedData,
          });
          
          localStorage.removeItem(KeysLocalStorage.DATA_ACCREDITED);
  
          toast.success('Credenciado registrado com sucesso');
          push(ACCREDITEDS_ROUTE);
        } catch (err: any) {
          console.log(err);
          let errors = {};
          if (err instanceof ValidationError) {
            errors = extractValidationErrors(err);
            formRef.current.setErrors(errors);
          } else {
            toast.error(err.message);
          }
        } finally {
          hideLoading();
        }
      }
      await runAsync();
    };

  useEffect(() => {
    showLoading();
    async function runAsync() {
      try {
        const programsResponse = await getPrograms.execute();  
        setPrograms(programsResponse);
      } catch (err) {
        console.log(err);
        toast.error('Falha ao carregar programas');
      } finally {
        hideLoading();
      }
    }

    runAsync();
  }, [getPrograms]);

  useEffect(() => {
    if (!id) return;

    showLoading();
    async function runAsync() {
      try {
        const programAccredited: ProgramAccredited = JSON.parse(localStorage.getItem(KeysLocalStorage.DATA_ACCREDITED) ?? '{}');
        const accredited = programAccredited.accredited;

        formRef.current.setData(accredited);
        if (programAccredited.program.id) {
          setSelectedProgram(programAccredited.program.id);
        }

        if (accredited.state) {
          setSelectedState(accredited.state);

          const stateObj = citiesDataList.find((s) => s.value === accredited.state);
          if (stateObj) {
            setAvailableCities(stateObj.cities);
          }
        }
        
        if (accredited.city) {
          setSelectedCity(accredited.city);
        }
        
        setIsProgramDisabled(true);
      } catch (err) {
        console.log(err);
        toast.error('Falha ao carregar credenciado');
        push(ACCREDITEDS_ROUTE);
      } finally {
        hideLoading();
      }
    }

    runAsync();
  }, [id, getAccredited, push]);

  const handleProgramChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const selectedValue = event.target.value;
    setSelectedProgram(selectedValue as string);
  };

  const handleStateChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const state = event.target.value as string;
    setSelectedState(state);
    setSelectedCity(''); 

    const stateObj = citiesDataList.find((s) => s.value === state);
    if (stateObj) {
      setAvailableCities(stateObj.cities);
    }
  };

  const handleCityChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setSelectedCity(event.target.value as string);
  };

  const onChangeCnpjInput = (event: React.ChangeEvent<{ value: unknown }>) => {
    const state = event.target.value as string;
    setCnpj(state);
  }

  useEffect(() => {
    const checkAndFetchData = async () => {
      if(id){
        setIsFieldsEnabled(true);
        setIsProgramDisabled(true);
        return;
      }

      if (CNPJ) {
        if (CNPJ.length === 18) {
          const cleanCnpj = removeMask(CNPJ);
          const accredited = await getAccreditedByCnpj.execute({ 
            CNPJ: cleanCnpj,
          });
  
          if (accredited) {
            formRef.current.setData(accredited);
  
            if (accredited.state) {
              setSelectedState(accredited.state);
  
              const stateObj = citiesDataList.find((s) => s.value === accredited.state);
              if (stateObj) {
                setAvailableCities(stateObj.cities);
              }
            }
            
            if (accredited.city) {
              setSelectedCity(accredited.city);
            }
  
            setIsFieldsEnabled(true);
            setIsProgramDisabled(false);
          } else {
            formRef.current.setData({
              CNPJ: CNPJ,
              corporateReason: '',
              fantasyName: '',
              CEP: '',
              street: '',
              neighborhood: '',
            });
  
            setSelectedState('');
            setSelectedCity('');
  
            setIsFieldsEnabled(true);
            setIsProgramDisabled(false);
          }
        }
      } else {
        setIsFieldsEnabled(false);
        setIsProgramDisabled(false);
      }
    };
  
    checkAndFetchData();
  }, [CNPJ]); // Dependências para disparar o useEffect

  return (
    <Box className={classes.container}>
      <PageTitle>{!id ? 'Cadastrar nova clínica credenciada' : 'Editar clínica credenciada'}</PageTitle>
      <br />
      <Form ref={formRef} onSubmit={handleSubmit}>
        <Paper className={classes.container}>
          <Grid container spacing={2} direction="row">
            <Grid item xs={12} sm={6} md={3} className={classes.gridItem}>
              <FormControl variant="outlined" fullWidth>
                <InputLabel>Programa</InputLabel>
                  <Select
                    label="Programa"
                    name="programId"
                    onChange={handleProgramChange}
                    disabled={isLoading || isProgramDisabled}
                    value={selectedProgram}
                    required={true}
                  >
                    {programs.map((program) => (
                      <MenuItem key={program.id} value={program.id}>
                        {program.name}
                      </MenuItem>
                    ))}
                  </Select>
              </FormControl>
            </Grid>
           <Grid item xs={12} sm={6} md={3} className={classes.gridItem}>
              <Input
                name="CNPJ"
                variant="outlined"
                label={'CNPJ'}
                className={classes.input}
                disabled={isLoading}
                mask={IMaskInputCNPJ}
                value={CNPJ}
                onChange={onChangeCnpjInput}
                required={true}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3} className={classes.gridItem}>
              <Input
                name="corporateReason"
                variant="outlined"
                label={'Razão corporativa'}
                className={classes.input}
                disabled={!isFieldsEnabled}
                required={true}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3} className={classes.gridItem}>
              <Input
                name="fantasyName"
                variant="outlined"
                label={'Nome fantasia'}
                className={classes.input}
                disabled={!isFieldsEnabled}
                required={true}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3} className={classes.gridItem}>
              <Input
                name="CEP"
                variant="outlined"
                label={'CEP'}
                className={classes.input}
                disabled={!isFieldsEnabled}
                required={true}
                mask={IMaskInputCEP}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3} className={classes.gridItem}>
              <FormControl variant="outlined" className={classes.formControl}>
                <InputLabel>UF</InputLabel>
                <Select
                  label="UF"
                  value={selectedState}
                  onChange={handleStateChange}
                  disabled={!isFieldsEnabled}
                >
                  {citiesDataList.map((state) => (
                    <MenuItem key={state.value} value={state.value}>
                      {state.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3} className={classes.gridItem}>
              <FormControl variant="outlined" className={classes.formControl}>
                <InputLabel>Cidade</InputLabel>
                <Select
                  label="Cidade"
                  value={selectedCity}
                  onChange={handleCityChange}
                  disabled={!selectedState}
                >
                  {availableCities.map((city) => (
                    <MenuItem key={city.value} value={city.value}>
                      {city.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3} className={classes.gridItem}>
              <Input
                name="street"
                variant="outlined"
                label={'Rua'}
                className={classes.input}
                disabled={!isFieldsEnabled}
                required={true}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3} className={classes.gridItem}>
              <Input
                name="neighborhood"
                variant="outlined"
                label={'Bairro'}
                className={classes.input}
                disabled={!isFieldsEnabled}
                required={true}
              />
            </Grid>
            <Grid item xs={12} className={classes.gridItem}>
              <Button type="submit" variant="contained" color="primary">
                {'Salvar'}
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Form>
    </Box>
  );  
}
